// Environmental Simulation System for Canntycoon
import { getStrain } from '../data/strains';

// ── VPD Calculation ──────────────────────────────────────────────────────────
export function calculateVPD(temp_c, humidity_rh) {
  const svp = 0.61078 * Math.exp((17.27 * temp_c) / (temp_c + 237.3));
  const avp = svp * (humidity_rh / 100.0);
  return Math.max(0, svp - avp);
}

// ── Environmental Drift ──────────────────────────────────────────────────────
// automationTier: 0=manual, 1=basic, 2=full, 3=AI
const AUTO_NOISE_MULTIPLIERS = [1.0, 0.70, 0.30, 0.08];

export function computeDrift(currentVal, targetVal, equipEfficiency, automationTier) {
  const tier = Math.min(3, Math.max(0, automationTier || 0));
  const correctionForce = (targetVal - currentVal) * equipEfficiency * 0.12;
  const noiseMagnitude = 0.20 * (1 - equipEfficiency) * AUTO_NOISE_MULTIPLIERS[tier];
  const noise = (Math.random() - 0.5) * 2 * noiseMagnitude;
  return correctionForce + noise;
}

// ── Plant Stress Calculation ─────────────────────────────────────────────────
// Returns a positive stress delta for this tick (or 0 when conditions are good)
export function calculateStressDelta(batch, environment, strain) {
  if (!batch || !strain) return 0;
  const baseStageData = strain[batch.stage === 'flowering' ? 'flower' : 'veg'];
  if (!baseStageData) return 0;

  // Seedlings are hearty — apply a wide forgiveness multiplier to all tolerances
  const seedlingForgiveness = batch.stage === 'seedling' ? 2.5 : 1.0;
  const stageData = {
    ...baseStageData,
    vpdTolerance:      baseStageData.vpdTolerance      * seedlingForgiveness,
    tempTolerance:     baseStageData.tempTolerance      * seedlingForgiveness,
    humidityTolerance: baseStageData.humidityTolerance  * seedlingForgiveness,
    co2Tolerance:      baseStageData.co2Tolerance       * seedlingForgiveness,
  };

  let stressDelta = 0;

  // VPD stress — scales with how far outside the tolerance band we are
  const currentVPD = calculateVPD(environment.temperature, environment.humidity);
  const vpdDev = Math.abs(currentVPD - stageData.vpdOptimal);
  if (vpdDev > stageData.vpdTolerance) {
    stressDelta += (vpdDev - stageData.vpdTolerance) * 3.0 * strain.pestSusceptibility;
  }

  // Temperature stress
  const tempDev = Math.abs(environment.temperature - stageData.tempOptimal);
  if (tempDev > stageData.tempTolerance) {
    stressDelta += (tempDev - stageData.tempTolerance) * 1.5;
  }

  // Humidity stress (secondary)
  const humDev = Math.abs(environment.humidity - stageData.humidityOptimal);
  if (humDev > stageData.humidityTolerance) {
    stressDelta += (humDev - stageData.humidityTolerance) * 0.5;
  }

  // CO2 deficiency stress
  if (environment.co2 < stageData.co2Optimal - stageData.co2Tolerance) {
    const deficit = (stageData.co2Optimal - stageData.co2Tolerance) - environment.co2;
    stressDelta += deficit * 0.002;
  }

  // Normalize to per-tick rate (1 game day = 24 ticks)
  return Math.min(stressDelta * (1 / 24), 3.0);
}

// ── Health Delta ─────────────────────────────────────────────────────────────
export function calculateHealthDelta(batch, stressDelta) {
  if (!batch) return 0;
  if (stressDelta > 0.1) {
    // Health loss scales with stress — more stressed = faster health loss
    return -Math.min(stressDelta * 1.5, 2.0);
  } else {
    // Recovery: slow when stressed, faster at low stress
    const recoverRate = Math.max(0, 0.15 - batch.stress * 0.001);
    return Math.min(recoverRate, 0.15);
  }
}

// ── Yield Potential Degradation ──────────────────────────────────────────────
export function calculateYieldDelta(batch, stressDelta) {
  // Only degrade yield under meaningful sustained stress (> 0.15 per-tick after normalization)
  if (!batch || stressDelta <= 0.15) return 0;
  // At harvest stage, cap drain to prevent zero-gram bug from room closures + understaffing
  const maxDrain = batch.stage === 'harvest' ? 0.05 : 0.5;
  // Yield degrades permanently with stress — cannot be recovered
  return -Math.min((stressDelta - 0.15) * 0.4, maxDrain);
}

// ── THC/Terpene Accumulation (flowering only) ────────────────────────────────
// Helper — true for strains that respond to cold-dark finish
function isIndicaDominant(strain) {
  return strain.genetics === 'indica' || strain.genetics === 'hybrid-indica';
}

export function calculatePotencyDelta(batch, environment, strain) {
  if (!batch || batch.stage !== 'flowering' || !strain) return { thc: 0, terpene: 0 };
  const flowerData = strain.flower;

  // Tight late-flower temp improves terpene expression
  const tempDevFromOptimal = Math.abs(environment.temperature - flowerData.tempOptimal);
  const terpeneGain = tempDevFromOptimal < 1 ? 0.03 : tempDevFromOptimal < 2 ? 0.015 : 0.005;

  // THC accumulates based on health
  const thcGain = ((batch.health / 100) * 0.025 * (strain.potencyMultiplier || 1.0)) || 0.02;

  // ── Indica cold-finish bonus ─────────────────────────────────────────────
  // Last 7 days of flowering + temp dropped ≥4°C below optimal = terpene burst
  // Risk: going that low (near/past tempTolerance) also adds stress — player's call
  const daysLeft = flowerData.days - batch.dayInStage;
  const tempDrop = flowerData.tempOptimal - environment.temperature; // positive = colder than optimal
  const coldFinishActive = isIndicaDominant(strain) && daysLeft <= 7 && tempDrop >= 4;

  const coldFinishTerpene  = coldFinishActive ? 0.12 : 0;  // extra terpene gain per tick
  const coldFinishAppeal   = coldFinishActive ? 0.06 : 0;  // visual appeal (purple/frost) per tick
  const coldFinishTHC      = coldFinishActive ? thcGain * 0.15 : 0; // small THC bump

  return {
    thc:    thcGain + coldFinishTHC,
    terpene: terpeneGain + coldFinishTerpene,
    visualAppealBonus: coldFinishAppeal,  // handled separately in updateRoomEnvironment
    coldFinishActive,
  };
}

// ── Mold Risk Accumulation ────────────────────────────────────────────────────
export function calculateMoldRiskDelta(batch, environment, strain, hasuv) {
  if (!batch || !strain) return 0;
  // Mold grows in high humidity above optimal for the stage
  const stageData = strain[batch.stage === 'flowering' ? 'flower' : 'veg'];
  if (!stageData) return 0;
  const humidityExcess = Math.max(0, environment.humidity - (stageData.humidityOptimal + 5));
  const baseMoldRate = strain.moldSusceptibility * humidityExcess * 0.005;
  const uvMultiplier = hasuv ? 0.40 : 1.0;
  return Math.min(baseMoldRate * uvMultiplier, 0.5);
}

// ── Herm Risk Accumulation ────────────────────────────────────────────────────
export function calculateHermRiskDelta(batch, stressDelta, strain, hasStabilization) {
  if (!batch || !strain || batch.stage !== 'flowering') return 0;
  if (stressDelta < 0.2) return 0;
  const baseHermRate = strain.hermRisk * stressDelta * 0.3;
  const stabilizationMultiplier = hasStabilization ? 0.50 : 1.0;
  return Math.min(baseHermRate * stabilizationMultiplier, 0.5);
}

// ── Equipment Degradation ────────────────────────────────────────────────────
export function checkEquipmentDegradation(room) {
  const newEquipment = { ...room.equipment };
  // Each piece of equipment degrades slowly — about 1% per 500 ticks on average
  const degradeCheck = (equip, key) => {
    if (!equip) return equip;
    if (Math.random() < 0.002) {
      return { ...equip, health: Math.max(0, equip.health - (0.5 + Math.random() * 1.5)) };
    }
    return equip;
  };

  return {
    ...newEquipment,
    hvac: degradeCheck(newEquipment.hvac, 'hvac'),
    lights: degradeCheck(newEquipment.lights, 'lights'),
    irrigation: degradeCheck(newEquipment.irrigation, 'irrigation'),
  };
}

// ── Effective Equipment Efficiency ───────────────────────────────────────────
export function effectiveEfficiency(equipment) {
  if (!equipment) return 0;
  // equipment health (0-100) scales actual efficiency; default 100 for old saves without health field
  const healthFactor = (equipment.health ?? 100) / 100;
  return (equipment.efficiency ?? 0) * healthFactor;
}

// ── Top-level per-room update ─────────────────────────────────────────────────
// Returns the new room state for a single tick
export function updateRoomEnvironment(room, unlockedTech) {
  // Lab rooms (and any future non-grow rooms) have no HVAC/lights — skip simulation
  if (room.type === 'lab' || room.type === 'curing' || room.type === 'processing' || !room.equipment?.hvac) return room;
  const hasAutoBasic   = unlockedTech.includes('A1');
  const hasAutoFull    = unlockedTech.includes('A3');
  const hasAI          = unlockedTech.includes('A4');
  const hasCO2         = unlockedTech.includes('A2');
  const hasUV          = unlockedTech.includes('B3');
  const hasStabilize   = unlockedTech.includes('C2');

  const autoTier = hasAI ? 3 : hasAutoFull ? 2 : hasAutoBasic ? 1 : 0;

  // ── Light level computation ────────────────────────────────────────────────
  // lightLevel (0.0–1.0) drives THC accumulation and indoor stress.
  // Greenhouse = free natural sunlight at 65% efficiency.
  // Indoor = needs installed + powered artificial lights or plants suffer.
  const isGreenhouse   = room.type === 'greenhouse';
  const lightsInstalled = room.equipment?.lights?.installed ?? false;
  // Power outage: artificial lights forced off even if lightOn=true
  const lightsOn        = room.poweredDown ? false : (room.environment?.lightOn ?? false);
  const artificialLightEff = lightsInstalled && lightsOn
    ? effectiveEfficiency(room.equipment.lights)
    : 0;
  // Artificial light contributes 70–100% based on efficiency; natural sunlight = 65%.
  const lightLevel = lightsInstalled && lightsOn
    ? Math.min(1.0, 0.70 + artificialLightEff * 0.30)
    : (isGreenhouse ? 0.65 : 0.05);

  // Compute current equipment efficiencies
  const hvacEff   = effectiveEfficiency(room.equipment.hvac);

  // Environmental drift
  // Power outage: HVAC offline — no correction, environment drifts uncontrolled
  let tempDrift, humDrift;
  if (room.poweredDown) {
    tempDrift = (Math.random() - 0.5) * 0.6; // ±0.3°C pure random noise per tick
    humDrift  = (Math.random() - 0.5) * 0.8; // ±0.4% RH pure random noise per tick
  } else {
    tempDrift = computeDrift(room.environment.temperature, room.recipe.temperature, hvacEff, autoTier);
    humDrift  = computeDrift(room.environment.humidity,    room.recipe.humidity,    hvacEff, autoTier);
  }

  let newEnv = {
    ...room.environment,
    temperature: Math.max(15, Math.min(35, room.environment.temperature + tempDrift)),
    humidity:    Math.max(20, Math.min(90, room.environment.humidity    + humDrift)),
  };

  // CO2 drifts toward atmospheric (400ppm) unless CO2 system installed
  if (hasCO2 && room.equipment.co2sys?.installed) {
    const co2Drift = computeDrift(newEnv.co2, room.recipe.co2, 0.85, autoTier);
    newEnv.co2 = Math.max(400, Math.min(1500, newEnv.co2 + co2Drift));
  } else {
    // Without CO2 system, passively drifts toward ambient
    newEnv.co2 = newEnv.co2 + (400 - newEnv.co2) * 0.01;
  }

  // Equipment degradation check
  const newEquipment = checkEquipmentDegradation(room);

  // Batch simulation
  let newBatch = room.batch;
  let batchStrain = null;
  let batchColdFinish = false;
  if (room.batch) {
    const strain = getStrain(room.batch.strainId);
    batchStrain = strain;
    if (strain) {
      let stressDelta = calculateStressDelta(room.batch, newEnv, strain);

      // ── Wrong light schedule penalty (flowering only) ──────────────────────
      // Flowering plants need 12/12. Any schedule > 13h during flower causes
      // serious herm risk and stress — player must manually flip the lights.
      const lightHours = room.recipe.lightHours;
      const wrongLightSchedule = room.batch.stage === 'flowering' && lightHours > 13;
      if (wrongLightSchedule) {
        // Extra stress: scales with how far off from 12h, normalized per tick
        const lightDeviation = lightHours - 12;
        stressDelta += (lightDeviation * 0.4) / 24;
      }

      // ── Indoor dark-room stress ────────────────────────────────────────────
      // Indoor rooms (not greenhouse) without installed + powered lights = pitch black.
      // Plants cannot survive; stress spikes hard to force player to buy lights.
      if (!isGreenhouse && lightLevel < 0.3) {
        stressDelta += 0.4;
      }

      // ── Equipment modifiers: stress dampening ─────────────────────────────
      // Stress Shield recipe (recipe.stressShield) reduces all env stress by 20%.
      const stressShieldFactor = room.recipe?.stressShield ? 0.80 : 1.0;
      // Smart irrigation (automationTier ≥ 1) cuts humidity-driven stress by 30%.
      const irrigationTier = room.equipment?.irrigation?.automationTier || 0;
      const irrigationFactor = irrigationTier >= 1 ? 0.70 : 1.0;
      // Apply combined dampening to get the effective stress this tick
      const effectiveStressDelta = stressDelta * stressShieldFactor * irrigationFactor;

      const healthDelta = calculateHealthDelta(room.batch, effectiveStressDelta);
      let yieldDelta    = calculateYieldDelta(room.batch, effectiveStressDelta);
      // Yield Maximizer recipe: slow yield potential recovery during vegetative stage
      if (room.recipe?.yieldMaximizer && room.batch.stage === 'vegetative') {
        yieldDelta += 0.008;
      }

      const moldDelta   = calculateMoldRiskDelta(room.batch, newEnv, strain, hasUV);
      // Wrong light schedule spikes herm risk directly on top of stress-based herm
      const hermDelta   = calculateHermRiskDelta(room.batch, effectiveStressDelta, strain, hasStabilize)
        + (wrongLightSchedule ? (lightHours - 12) * 0.08 / 24 : 0);
      const { thc: rawTHC, terpene: rawTerpene, visualAppealBonus = 0, coldFinishActive = false } = calculatePotencyDelta(room.batch, newEnv, strain);
      batchColdFinish = coldFinishActive;

      // ── Light scaling for THC ──────────────────────────────────────────────
      // Light drives photosynthesis → potency. THC scales directly with lightLevel.
      // Premium LEDs (efficiency >= 0.85) add a +10% THC bonus from spectral quality.
      const thcLightBonus = (lightsInstalled && lightsOn && artificialLightEff >= 0.85) ? 1.10 : 1.0;
      // Pheno bonus: multiplier on THC and terpene gain per tick
      const phenoBonuses = room.batch.phenoBonuses || null;
      const thcPhenoMultiplier  = 1 + (phenoBonuses?.thc     ?? 0);
      const terpPhenoMultiplier = 1 + (phenoBonuses?.terpene ?? 0);
      const thc = rawTHC * lightLevel * thcLightBonus * thcPhenoMultiplier;
      // Bloom Boost recipe: extra terpene accumulation during flowering stage
      const bloomBoostBonus = (room.recipe?.bloomBoost && room.batch.stage === 'flowering') ? 0.02 : 0;
      const terpene = (rawTerpene + bloomBoostBonus) * terpPhenoMultiplier;

      // Stress recovers when conditions are good; accumulates when they're not
      const stressChange = effectiveStressDelta < 0.02
        ? -Math.max(0.05, 0.3 - room.batch.stress * 0.003)  // recovery: faster when stress is low
        : effectiveStressDelta;
      const newStress = Math.max(0, Math.min(100, room.batch.stress + stressChange));
      const newHealth       = Math.max(0, Math.min(100, room.batch.health + healthDelta));
      const newYieldPot     = Math.max(0, Math.min(100, room.batch.yieldPotential + yieldDelta));
      const newMoldRisk     = Math.max(0, Math.min(100, room.batch.moldRisk + moldDelta));
      const newHermRisk     = Math.max(0, Math.min(100, room.batch.hermaphroditeRisk + hermDelta));
      const newTHC          = Math.min(100, room.batch.thcAccumulation + thc);
      const newTerpene      = Math.min(100, room.batch.terpeneProfile + terpene);

      // Visual appeal: degrades with herm/mold, gains from cold-finish technique
      const appealLoss = (hermDelta + moldDelta) * 2;
      const newVisualAppeal = Math.max(0, Math.min(100, room.batch.visualAppeal - appealLoss + visualAppealBonus));

      // Pest risk accumulates per-tick during veg and flower stages; good VPD provides passive decay
      const inGrowStage = room.batch.stage === 'vegetative' || room.batch.stage === 'flowering';
      const pestGain = inGrowStage && strain.pestSusceptibility ? strain.pestSusceptibility * 0.3 : 0;
      const stageDataForPest = strain[room.batch.stage === 'flowering' ? 'flower' : 'veg'];
      const currentVPDForPest = calculateVPD(newEnv.temperature, newEnv.humidity);
      const vpdOkForPest = stageDataForPest
        ? Math.abs(currentVPDForPest - stageDataForPest.vpdOptimal) < stageDataForPest.vpdTolerance
        : false;
      const pestDecay = vpdOkForPest ? 0.05 : 0;
      const newPestRisk = Math.min(100, Math.max(0, (room.batch.pestRisk || 0) + pestGain - pestDecay));

      // Projected grams calculation
      const yieldBonus = room.batch.phenoBonuses?.yield ?? 0;
      const projGrams = strain.yieldCeiling * (1 + yieldBonus)
        * room.batch.plantCount
        * Math.pow(newHealth / 100, 1.5)
        * Math.pow(1 - newStress / 100, 1.2)
        * (newYieldPot / 100);

      newBatch = {
        ...room.batch,
        stress: newStress,
        health: newHealth,
        yieldPotential: newYieldPot,
        moldRisk: newMoldRisk,
        hermaphroditeRisk: newHermRisk,
        thcAccumulation: newTHC,
        terpeneProfile: newTerpene,
        visualAppeal: newVisualAppeal,
        pestRisk: newPestRisk,
        projectedGrams: Math.round(projGrams),
        totalAge: room.batch.totalAge + (1 / 24), // 1 tick = 1 hour = 1/24 day
        dayInStage: room.batch.dayInStage + (1 / 24),
      };
    }
  }

  // Build alert list from current state
  const alerts = buildAlerts(newEnv, newBatch, room, batchStrain, batchColdFinish);

  return {
    ...room,
    environment: newEnv,
    equipment: newEquipment,
    batch: newBatch,
    alerts,
  };
}

// ── Alert Builder ─────────────────────────────────────────────────────────────
function buildAlerts(environment, batch, room, strain, coldFinishActive = false) {
  const alerts = [];
  const vpd = calculateVPD(environment.temperature, environment.humidity);

  if (environment.temperature > 30) alerts.push({ type: 'warning', message: `High temp: ${environment.temperature.toFixed(1)}°C`, severity: 'high' });
  if (environment.temperature < 18) alerts.push({ type: 'warning', message: `Low temp: ${environment.temperature.toFixed(1)}°C`, severity: 'high' });
  if (environment.humidity > 70)    alerts.push({ type: 'warning', message: `High humidity: ${environment.humidity.toFixed(0)}% RH`, severity: 'medium' });

  if (vpd > 1.8)                    alerts.push({ type: 'vpd',     message: `VPD too high: ${vpd.toFixed(2)} kPa`, severity: 'high' });
  if (vpd < 0.4)                    alerts.push({ type: 'vpd',     message: `VPD too low: ${vpd.toFixed(2)} kPa`, severity: 'medium' });

  if ((room.equipment?.hvac?.health ?? 100) < 30)   alerts.push({ type: 'equipment', message: 'HVAC failing — needs repair', severity: 'high' });
  if ((room.equipment?.lights?.health ?? 100) < 30 && room.equipment?.lights?.installed) alerts.push({ type: 'equipment', message: 'Lights failing — needs repair', severity: 'high' });

  // ── Light installation alerts ──────────────────────────────────────────────
  if (!room.equipment?.lights?.installed) {
    if (room.type !== 'greenhouse') {
      alerts.push({ type: 'equipment', message: '⚠ No grow lights installed! Plants are dying in the dark. Buy lights in Equipment Shop.', severity: 'critical' });
    } else {
      // Greenhouse: natural light works but is limiting — informational, not critical
      alerts.push({ type: 'tip', message: '💡 Running on natural light — THC limited to ~65%. Buy LED lights in Equipment Shop for full potency.', severity: 'info' });
    }
  }

  if (batch) {
    // Transfer window alert — remind player to move veg batch to a flower room
    if (strain && batch.stage === 'vegetative') {
      const vegDays = strain.veg?.days || 28;
      const daysLeft = vegDays - batch.dayInStage;
      if (daysLeft <= 5 && daysLeft > 0) {
        alerts.push({ type: 'tip', message: `🌸 ${Math.ceil(daysLeft)}d left in veg — transfer to a flower room soon for best quality!`, severity: 'info' });
      } else if (daysLeft <= 0) {
        alerts.push({ type: 'tip', message: `🌸 Veg stage ending — transfer to flower room or flip lights to 12/12 now!`, severity: 'high' });
      }
    }

    // Cold-finish opportunity — prompt player when entering last week for indicas
    if (strain && (strain.genetics === 'indica' || strain.genetics === 'hybrid-indica')
        && batch.stage === 'flowering') {
      const daysLeft = (strain.flower?.days || 56) - batch.dayInStage;
      const tempDrop = (strain.flower?.tempOptimal || 22) - environment.temperature;
      if (daysLeft <= 7 && daysLeft > 0 && !coldFinishActive) {
        alerts.push({ type: 'tip', message: `❄️ Final week — drop temps ${Math.ceil(4 - tempDrop)}°C more for indica cold-finish terpene burst!`, severity: 'info' });
      }
      if (coldFinishActive) {
        alerts.push({ type: 'tip', message: `❄️ Cold finish active — terpenes & appeal boosting!`, severity: 'success' });
      }
    }

    // Wrong light schedule alert — highest priority for flowering
    if (batch.stage === 'flowering' && room.recipe.lightHours > 13) {
      alerts.push({ type: 'lights', message: `⚠ Flip lights to 12/12! Plants are flowering on ${room.recipe.lightHours}h schedule — herm risk climbing!`, severity: 'critical' });
    }
    if (batch.health < 40)              alerts.push({ type: 'plant', message: `Plant health critical: ${batch.health.toFixed(0)}%`, severity: 'critical' });
    if (batch.moldRisk > 50)            alerts.push({ type: 'mold',  message: `High mold risk: ${batch.moldRisk.toFixed(0)}%`, severity: 'high' });
    if (batch.hermaphroditeRisk > 40)   alerts.push({ type: 'herm',  message: `Herm risk elevated: ${batch.hermaphroditeRisk.toFixed(0)}%`, severity: 'high' });
  }

  return alerts;
}

// ── Stage Advance Check ──────────────────────────────────────────────────────
// Returns the new stage if the batch should advance, or null
export function checkStageAdvance(batch, strain) {
  if (!batch || !strain) return null;
  const vegDaysRequired    = Math.round(strain.veg.days    / (strain.growthSpeed || 1.0));
  const flowerDaysRequired = Math.round(strain.flower.days / (strain.growthSpeed || 1.0));
  const transitions = {
    seedling:   { next: 'vegetative', daysRequired: 3 },
    vegetative: { next: 'flowering',  daysRequired: vegDaysRequired },
    flowering:  { next: 'harvest',    daysRequired: flowerDaysRequired },
  };
  const transition = transitions[batch.stage];
  if (!transition) return null;
  if (batch.dayInStage >= transition.daysRequired) return transition.next;
  return null;
}

// ── Final Harvest Quality Score ───────────────────────────────────────────────
export function calculateHarvestQuality(batch, strain) {
  if (!batch || !strain) return 0;
  const thcScore    = Math.min(100, (batch.thcAccumulation / strain.thcCeiling) * 100);
  const terpScore   = batch.terpeneProfile;
  const visualScore = batch.visualAppeal;
  // Weighted: THC 40%, terpene 35%, visual 25%
  return Math.round(thcScore * 0.40 + terpScore * 0.35 + visualScore * 0.25);
}

// ── Final Harvest Grams ───────────────────────────────────────────────────────
export function calculateFinalYield(batch, strain, workerSkill) {
  if (!batch || !strain) return 0;
  const trimFactor = 0.85 + (workerSkill / 100) * 0.15; // 0.85–1.0
  const yieldBonus = batch.phenoBonuses?.yield ?? 0;
  const effectiveYieldCeiling = strain.yieldCeiling * (1 + yieldBonus);
  const pestPenalty = (batch.pestRisk || 0) > 50
    ? 1 - (((batch.pestRisk || 0) - 50) / 100) * 0.3
    : 1.0;
  const finalGrams = effectiveYieldCeiling
    * batch.plantCount
    * Math.pow(batch.health / 100, 1.5)
    * Math.pow(1 - batch.stress / 100, 1.2)
    * (batch.yieldPotential / 100)
    * trimFactor
    * pestPenalty;
  return Math.max(0, Math.round(finalGrams));
}
